import { useCallback, useEffect, useRef } from 'react';
import { useResponsive } from './useResponsive';

/**
 * 用户行为追踪Hook
 * 提供完整的用户行为分析和数据收集功能
 */
export const useAnalytics = () => {
  const { screenSize, isMobile } = useResponsive();
  const sessionStartTime = useRef(Date.now());
  const pageViewStartTime = useRef(Date.now());
  const interactionCount = useRef(0);

  // 获取用户会话信息
  const getSessionInfo = useCallback(() => {
    return {
      sessionId: sessionStorage.getItem('session_id') || generateSessionId(),
      userId: localStorage.getItem('user_id') || generateUserId(),
      sessionDuration: Date.now() - sessionStartTime.current,
      pageViewDuration: Date.now() - pageViewStartTime.current,
      interactionCount: interactionCount.current,
      deviceInfo: {
        screenSize,
        isMobile,
        userAgent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    };
  }, [screenSize, isMobile]);

  // 生成会话ID
  const generateSessionId = useCallback(() => {
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('session_id', sessionId);
    return sessionId;
  }, []);

  // 生成用户ID
  const generateUserId = useCallback(() => {
    const userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('user_id', userId);
    return userId;
  }, []);

  // 基础事件追踪
  const trackEvent = useCallback((eventName, properties = {}) => {
    const eventData = {
      event: eventName,
      properties: {
        ...properties,
        ...getSessionInfo(),
        timestamp: new Date().toISOString(),
        url: window.location.href,
        referrer: document.referrer
      }
    };

    // 发送到Google Analytics
    if (window.gtag) {
      window.gtag('event', eventName, {
        custom_map: {
          event_data: JSON.stringify(eventData)
        }
      });
    }

    // 发送到自定义分析服务
    if (window.analytics && typeof window.analytics.track === 'function') {
      window.analytics.track(eventName, eventData.properties);
    }

    // 开发环境日志
    if (process.env.NODE_ENV === 'development') {
      console.log('Analytics Event:', eventData);
    }

    // 更新交互计数
    interactionCount.current++;
  }, [getSessionInfo]);

  // 页面浏览追踪
  const trackPageView = useCallback((pageName, properties = {}) => {
    pageViewStartTime.current = Date.now();
    
    trackEvent('page_view', {
      page_name: pageName,
      page_title: document.title,
      ...properties
    });
  }, [trackEvent]);

  // 用户交互追踪
  const trackInteraction = useCallback((interactionType, element, properties = {}) => {
    trackEvent('user_interaction', {
      interaction_type: interactionType,
      element_type: element?.tagName?.toLowerCase(),
      element_id: element?.id,
      element_class: element?.className,
      element_text: element?.textContent?.slice(0, 100),
      ...properties
    });
  }, [trackEvent]);

  // 转化事件追踪
  const trackConversion = useCallback((conversionType, value = 1, properties = {}) => {
    trackEvent('conversion', {
      conversion_type: conversionType,
      conversion_value: value,
      ...properties
    });
  }, [trackEvent]);

  // 错误追踪
  const trackError = useCallback((error, context = {}) => {
    trackEvent('error', {
      error_message: error.message,
      error_stack: error.stack,
      error_name: error.name,
      context,
      ...getSessionInfo()
    });
  }, [trackEvent, getSessionInfo]);

  // 性能追踪
  const trackPerformance = useCallback((metricName, value, properties = {}) => {
    trackEvent('performance', {
      metric_name: metricName,
      metric_value: value,
      ...properties
    });
  }, [trackEvent]);

  // 初始化会话
  useEffect(() => {
    generateSessionId();
    generateUserId();
  }, [generateSessionId, generateUserId]);

  return {
    trackEvent,
    trackPageView,
    trackInteraction,
    trackConversion,
    trackError,
    trackPerformance,
    getSessionInfo
  };
};

/**
 * 页面停留时间追踪Hook
 */
export const usePageTimeTracking = (pageName) => {
  const { trackEvent } = useAnalytics();
  const startTime = useRef(Date.now());
  const isVisible = useRef(true);
  const totalTime = useRef(0);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 页面隐藏，记录当前停留时间
        totalTime.current += Date.now() - startTime.current;
        isVisible.current = false;
      } else {
        // 页面显示，重新开始计时
        startTime.current = Date.now();
        isVisible.current = true;
      }
    };

    const handleBeforeUnload = () => {
      // 页面卸载前记录总停留时间
      if (isVisible.current) {
        totalTime.current += Date.now() - startTime.current;
      }
      
      trackEvent('page_time', {
        page_name: pageName,
        time_spent: totalTime.current,
        time_spent_seconds: Math.round(totalTime.current / 1000)
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      handleBeforeUnload(); // 确保记录时间
    };
  }, [pageName, trackEvent]);

  return {
    getCurrentTime: () => {
      const current = isVisible.current ? Date.now() - startTime.current : 0;
      return totalTime.current + current;
    }
  };
};

/**
 * 滚动深度追踪Hook
 */
export const useScrollTracking = (pageName) => {
  const { trackEvent } = useAnalytics();
  const maxScrollDepth = useRef(0);
  const milestones = useRef(new Set());

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercentage = Math.round((scrollTop / documentHeight) * 100);

      // 更新最大滚动深度
      if (scrollPercentage > maxScrollDepth.current) {
        maxScrollDepth.current = scrollPercentage;
      }

      // 追踪滚动里程碑
      const milestonePoints = [25, 50, 75, 90, 100];
      milestonePoints.forEach(point => {
        if (scrollPercentage >= point && !milestones.current.has(point)) {
          milestones.current.add(point);
          trackEvent('scroll_milestone', {
            page_name: pageName,
            scroll_depth: point,
            max_scroll_depth: maxScrollDepth.current
          });
        }
      });
    };

    const throttledScroll = throttle(handleScroll, 500);
    window.addEventListener('scroll', throttledScroll);

    return () => {
      window.removeEventListener('scroll', throttledScroll);
      
      // 记录最终滚动深度
      trackEvent('scroll_depth_final', {
        page_name: pageName,
        max_scroll_depth: maxScrollDepth.current
      });
    };
  }, [pageName, trackEvent]);

  return {
    getMaxScrollDepth: () => maxScrollDepth.current,
    getMilestones: () => Array.from(milestones.current)
  };
};

/**
 * 点击热力图追踪Hook
 */
export const useClickTracking = () => {
  const { trackInteraction } = useAnalytics();

  useEffect(() => {
    const handleClick = (event) => {
      const element = event.target;
      const rect = element.getBoundingClientRect();
      
      trackInteraction('click', element, {
        click_x: event.clientX,
        click_y: event.clientY,
        element_x: rect.left,
        element_y: rect.top,
        element_width: rect.width,
        element_height: rect.height,
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight
      });
    };

    document.addEventListener('click', handleClick);
    
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [trackInteraction]);
};

// 工具函数：节流
const throttle = (func, limit) => {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

export default useAnalytics;
